import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_PRESET_MS } from '../rules/replayPresets';

/**
 * Per-rule lifecycle status.
 * - `idle`     — not running; a live rule can Start, a single-shot can Fetch.
 * - `running`  — live rule ticking (client engine owns the timer).
 * - `paused`   — live rule stopped mid-run; cursor + alerts kept, resumes on Start.
 * - `fetching` — single-shot request in flight.
 * - `frozen`   — single-shot cooldown (5s) before it can refetch.
 */
export type RuleStatus = 'idle' | 'running' | 'paused' | 'fetching' | 'frozen';

export interface RuleRuntime {
  status: RuleStatus;
  /** Picked window size in ms (REPLAY_PRESETS). Survives Stop. */
  windowMs: number;
  /**
   * Custom absolute [from, to] window (ISO 8601). null ⇒ use relative `windowMs`.
   * Sets the initial backfill window; a live rule then ticks forward like a preset.
   * Survives Stop, just like `windowMs`.
   */
  custom: { from: string; to: string } | null;
  /** Live tick cursor (ISO `to` of the last tick). null ⇒ next tick backfills `now - windowMs`. */
  cursorIso: string | null;
  lastRunAt: string | null;
  error: string | null;
}

interface DetectionState {
  runtime: Record<string, RuleRuntime>;
}

const initialState: DetectionState = { runtime: {} };

const blank = (): RuleRuntime => ({
  status: 'idle',
  windowMs: DEFAULT_PRESET_MS,
  custom: null,
  cursorIso: null,
  lastRunAt: null,
  error: null,
});

const ensure = (state: DetectionState, ruleId: string): RuleRuntime =>
  (state.runtime[ruleId] ??= blank());

const detectionSlice = createSlice({
  name: 'detection',
  initialState,
  reducers: {
    /** Pick the window size for a rule (only meaningful while idle). */
    windowPicked(state, action: PayloadAction<{ ruleId: string; windowMs: number }>) {
      ensure(state, action.payload.ruleId).windowMs = action.payload.windowMs;
    },
    /** Set a custom absolute [from, to] window (ISO 8601). Survives Stop. */
    customWindowSet(state, action: PayloadAction<{ ruleId: string; from: string; to: string }>) {
      ensure(state, action.payload.ruleId).custom = {
        from: action.payload.from,
        to: action.payload.to,
      };
    },
    /** Clear the custom window — fall back to the relative `windowMs`. */
    customWindowCleared(state, action: PayloadAction<string>) {
      ensure(state, action.payload).custom = null;
    },
    /**
     * Start / resume a live rule. Deliberately does NOT touch `cursorIso`:
     * null (fresh start) ⇒ backfill the window; non-null (resume from pause) ⇒
     * the engine ticks `[cursor, now]`, backfilling the paused gap.
     */
    started(state, action: PayloadAction<string>) {
      const r = ensure(state, action.payload);
      r.status = 'running';
      r.error = null;
    },
    /** Pause a running rule — keep the cursor + its alerts. */
    paused(state, action: PayloadAction<string>) {
      const r = ensure(state, action.payload);
      if (r.status === 'running') r.status = 'paused';
    },
    /** A live tick succeeded — advance the cursor. */
    tickAdvanced(state, action: PayloadAction<{ ruleId: string; cursorIso: string; ranAt: string }>) {
      const r = ensure(state, action.payload.ruleId);
      r.cursorIso = action.payload.cursorIso;
      r.lastRunAt = action.payload.ranAt;
      r.error = null;
    },
    /** A live tick failed — stay running so the next tick retries the same window. */
    tickFailed(state, action: PayloadAction<{ ruleId: string; error: string; ranAt: string }>) {
      const r = ensure(state, action.payload.ruleId);
      r.error = action.payload.error;
      r.lastRunAt = action.payload.ranAt;
    },
    /** Stop a rule — back to idle, cursor cleared (alerts are cleared separately). Window kept. */
    stopped(state, action: PayloadAction<string>) {
      const r = ensure(state, action.payload);
      r.status = 'idle';
      r.cursorIso = null;
      r.error = null;
      r.lastRunAt = null;
    },
    /** Kick off a single-shot fetch. */
    fetchStarted(state, action: PayloadAction<string>) {
      const r = ensure(state, action.payload);
      r.status = 'fetching';
      r.error = null;
    },
    /** Single-shot finished (ok or error) — enter the 5s cooldown. */
    frozen(state, action: PayloadAction<{ ruleId: string; ranAt: string }>) {
      const r = ensure(state, action.payload.ruleId);
      r.status = 'frozen';
      r.lastRunAt = action.payload.ranAt;
    },
    /** Cooldown elapsed — single-shot can fetch again. */
    idled(state, action: PayloadAction<string>) {
      ensure(state, action.payload).status = 'idle';
    },
  },
});

export const {
  windowPicked,
  customWindowSet,
  customWindowCleared,
  started,
  paused,
  tickAdvanced,
  tickFailed,
  stopped,
  fetchStarted,
  frozen,
  idled,
} = detectionSlice.actions;
export default detectionSlice.reducer;
